import React from 'react';
import {
    Table,
    TableColumn,
    TableHeaderTheme,
    TableRow,
    TableRowTheme,
    TableBorderRadius,
    TableBorderStyle,
    TableRowSeperatorStyle,
} from '@apply/components';
import { GCRoleType, GCStatusType } from '@apply/constants';
import { AuthorizedDrawUserDto } from '@apply/api/src/api/ApplyApi';
import { GeneralContractorRole } from './components/GeneralContractorRole';
import { EmailLink } from './components/EmailLink';
import { StatusIndicator } from './components/StatusIndicator';
import { GCUserLink } from './components/GCUserLink';
import { ActionsButton } from './components/ActionsButton/ActionsButton';
import { Phone } from './components/Phone';

export interface ByUserTableProps {
    users: AuthorizedDrawUserDto[];
    onRefresh: () => void;
}

export const ByUserTable: React.FC<ByUserTableProps> = ({ users, onRefresh }) => {
    const renderName = (user: AuthorizedDrawUserDto) => {
        return (
            <GCUserLink
                authorizedDrawUserId={user.id}
                firstName={user.displayFirstName || ''}
                lastName={user.displayLastName || ''}
                onRefresh={onRefresh}
            />
        );
    };

    const renderRole = (role?: GCRoleType) => {
        return <GeneralContractorRole role={role} />;
    };

    const renderEmailAddress = (emailAddress?: string) => {
        return <EmailLink emailAddress={emailAddress || ''} />;
    };

    const renderStatus = (status: GCStatusType) => {
        return <StatusIndicator status={status} />;
    };

    const renderActions = (user: AuthorizedDrawUserDto) => {
        return <ActionsButton user={user} onRefresh={onRefresh} />;
    };

    const renderPhone = (phone: string) => {
        return <Phone phoneNumber={phone} />;
    };

    const renderProjectCount = (count: number) => {
        return <span className='manageUsersByUserTable-generalContractor-projects'>{count}</span>;
    };

    const data: TableRow[] = users?.map((user) => {
        let status: GCStatusType;

        // 1) 'Invited' - Has AuthorizedDrawUser record, but has not yet registered their account
        if (user.needsPasswordReset) {
            status = GCStatusType.Invited;
        }
        // 2) 'Needs Project' - has registered account but not assigned to any properties
        else if (!user.hasPropertyAssignment) {
            status = GCStatusType.NeedsProject;
        }
        // 3) 'Pending GC' - registered and assigned to properties, but not yet accepted
        else if (!user.gcAcceptedUtc) {
            status = GCStatusType.Pending;
        }
        // 4) 'Activated' - meets all criteria
        else {
            status = GCStatusType.Activated;
        }

        return {
            userId: user.userId,
            name: renderName(user),
            role: renderRole(user.displayRole),
            phone: renderPhone(user.displayPhone || ''),
            emailAddress: renderEmailAddress(user.email || ''),
            projectCount: renderProjectCount(user.projectCount),
            status: renderStatus(status),
            actions: renderActions(user),
        };
    });

    return (
        <>
            <div className='byUserPanel'>
                <Table
                    headerTheme={TableHeaderTheme.Gray}
                    rowTheme={TableRowTheme.Gray}
                    borderRadius={TableBorderRadius.Small}
                    borderStyle={TableBorderStyle.Solid}
                    showHeader={true}
                    data={data}
                    width='100%'
                    rowSeperatorStyle={TableRowSeperatorStyle.Medium}
                >
                    <TableColumn
                        columnKey='role'
                        header='Role'
                        width='10%'
                    />
                    <TableColumn
                        columnKey='name'
                        header='Name'
                        width='20%'
                    />
                    <TableColumn
                        columnKey='phone'
                        header='Phone Number'
                        width='10%'
                    />
                    <TableColumn
                        columnKey='emailAddress'
                        header='Email Address'
                        width='25%'
                    />
                    <TableColumn
                        columnKey='projectCount'
                        header='Projects'
                        width='10%'
                    />
                    <TableColumn
                        columnKey='status'
                        header='Status'
                        width='10%'
                    />
                    <TableColumn
                        columnKey='actions'
                        width='5%'
                        actionColumn={true}
                    />
                </Table>
            </div>
        </>
    );
};
